import axios, { AxiosInstance } from "axios";
import { API_URL } from "constants/api";
import { appStore } from "store";
import { userStore } from "store/user";
import { BonusProgram, ReferralProgram, Round, Settings } from "types";
import {
  IPaymentLimit,
  Order,
  OrdersResponse,
  SaleProgressResponse,
  SaleProgressApiResponse,
} from "types/Tokens";

export interface PromocodeValidationResponse {
  isValid: boolean;

  code: string | null;
  description: string | null;
  bonusPercentage: number | null;
  minPurchase: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
}

export class TokensService {
  private _api: AxiosInstance;

  constructor() {
    this._api = axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
  }

  async getCurrentRound() {
    try {
      appStore.setState({ isLoading: true });
      const url = "/api/rounds/current";
      const { data } = await this._api.get<Round>(url);

      const raw = data as Round & Record<string, unknown>;

      const toNum = (v: unknown): number | undefined => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string" && v.trim() !== "") {
          const n = Number(v);
          return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
      };

      const tokenPrice =
        toNum(raw.tokenPrice) ??
        toNum(raw.token_price) ??
        toNum(raw.price) ??
        (typeof data.tokenPrice === "number" && Number.isFinite(data.tokenPrice)
          ? data.tokenPrice
          : toNum(data.tokenPrice as unknown));

      const normalized: Round = {
        ...data,
        tokenPrice:
          tokenPrice ??
          toNum(data.tokenPrice as unknown) ??
          (typeof data.tokenPrice === "number" ? data.tokenPrice : 0),
        nextTokenPrice:
          toNum(raw.nextTokenPrice) ??
          toNum(raw.next_token_price) ??
          toNum(raw.nextPrice),
        listingTokenPrice:
          toNum(raw.listingTokenPrice) ??
          toNum(raw.listing_token_price) ??
          toNum(raw.listingPrice),
      };

      appStore.setState({ currentRound: normalized, isLoading: false });

      return normalized;
    } catch (error) {
      console.warn(error);
    }
    return null;
  }

  async sendMessage(
    name: string,
    email: string,
    message: string
  ): Promise<void> {
    try {
      const url = "/api/mail/send-message";
      await this._api.post(url, { name, email, message });
    } catch (error) {
      console.warn(error);
    }
  }

  async sendOrder(
    tokensAmount: number,
    paymentMethod: string,
    referralCode: string | undefined,
    promocode?: string
  ): Promise<OrdersResponse | null> {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("No access token");
      }

      const { currentRound } = appStore.getState();

      if (!currentRound) return null;

      const { data } = await this._api.post<OrdersResponse>(
        "/api/orders",
        {
          tokensAmount,
          paymentMethod,
          roundId: currentRound.id,
          referralCode,
          promocode,
        },
        {
          headers: {
            authorization: accessToken,
          },
        }
      );

      return data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getOrderById(orderId: string): Promise<Order | null> {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("No access token");
      }

      const { data } = await this._api.get<Order>(`/api/orders/${orderId}`, {
        headers: {
          authorization: accessToken,
        },
      });

      return data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getSaleProgress(): Promise<SaleProgressResponse | null> {
    try {
      const url = "/api/balances/sale-progress";
      const { data } = await this._api.get<SaleProgressResponse>(url);

      return data;
    } catch (error) {
      console.warn(error);
    }

    return null;
  }

  async getTokensBalance(): Promise<number> {
    try {
      const url = "/api/balances/my-tokens";

      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("No access token");
      }

      const { data } = await this._api.get<{ amount: number }>(url, {
        headers: {
          authorization: accessToken,
        },
      });

      return data.amount;
    } catch (error) {
      console.warn(error);
    }
    return 0;
  }

  async getMarkleTreeProof(): Promise<string[] | null> {
    const url = "/api/merkle-tree/proof";
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      throw new Error("No access token");
    }

    const { data } = await this._api.get<string[]>(url, {
      headers: {
        authorization: accessToken,
      },
    });

    return data;
  }

  async getSalesEndTimestamp(): Promise<number> {
    try {
      const url = "/api/sale/end-timestamp";
      const { data } = await this._api.get<{ endTimestamp: number }>(url);

      appStore.setState({ endOfSalesTimestamp: data.endTimestamp });

      return data.endTimestamp;
    } catch (error) {
      console.warn(error);
    }
    return 0;
  }

  async getCurrentBonus() {
    try {
      const url = "/api/bonuses/current";
      const { data } = await this._api.get<BonusProgram>(url);

      appStore.setState({ currentBonus: data });

      return data;
    } catch (error) {
      console.warn(error);
    }
    return null;
  }

  async getCurrentReferralProgram() {
    try {
      const url = "/api/referral-bonuses/current";
      const { data } = await this._api.get<ReferralProgram>(url);

      appStore.setState({ currentReferralProgram: data });

      return data;
    } catch (error) {
      console.warn(error);
    }
    return null;
  }

  async isRefAvailable() {
    try {
      const token = localStorage.getItem("accessToken");
      const { data } = await this._api.get<{ available: boolean }>(
        "/api/referral-bonuses/check-available",
        {
          headers: {
            authorization: token,
          },
        }
      );

      const currentReferralProgram = await this.getCurrentReferralProgram();

      userStore.setState({
        canEnterRefCode: currentReferralProgram?.enabled && data.available,
      });

      return data.available;
    } catch (error) {
      console.warn(error);
    }
    return false;
  }

  async isRefCodeValid(refCode: string) {
    const token = localStorage.getItem("accessToken");
    try {
      const { data } = await this._api.get<{ available: boolean }>(
        `/api/referral-bonuses/check-code`,
        {
          params: {
            code: refCode,
          },
          headers: {
            authorization: token,
          },
        }
      );
      return data.available;
    } catch {
      console.error("Invalid referral code");
    }
  }

  async validatePromocode(
    promoCode: string
  ): Promise<PromocodeValidationResponse | null> {
    const token = localStorage.getItem("accessToken");
    const encodedCode = encodeURIComponent(promoCode);

    try {
      const { data } = await this._api.get<PromocodeValidationResponse>(
        `/api/promocode/${encodedCode}`,
        {
          headers: {
            authorization: token,
          },
        }
      );

      return data;
    } catch (error) {
      return null;
    }
  }

  async getProgress() {
    try {
      const { setProgress } = appStore.getState();

      const url = "/api/sale/progress";
      const { data } = await this._api.get<SaleProgressApiResponse>(url);
      const raw = data as SaleProgressApiResponse & Record<string, unknown>;

      const toN = (v: unknown): number | undefined => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string" && v.trim() !== "") {
          const n = Number(v);
          return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
      };

      const targetCap =
        toN(raw.targetUsd) ??
        toN(raw.target_usd) ??
        toN(raw.totalUsd) ??
        toN(raw.total_usd) ??
        toN(data.total);

      const raised =
        toN(raw.raisedUsd) ??
        toN(raw.raised_usd) ??
        toN(raw.soldUsd) ??
        toN(raw.sold_usd) ??
        toN(data.sold);

      const total = targetCap ?? toN(data.total) ?? 0;
      const sold = raised ?? toN(data.sold) ?? 0;

      setProgress(total, sold);

      return { total, sold, raw: data };
    } catch (error) {
      console.warn(error);
    }
  }

  async getSettings() {
    try {
      const url = "/api/settings";
      const { data } = await this._api.get<Settings>(url);

      appStore.setState({
        settings: data,
      });

      return data;
    } catch (error) {
      console.warn(error);
    }
  }

  async getPaymentLimit(token: string): Promise<IPaymentLimit> {
    const url = "/api/now-payments/limits";

    const { data } = await this._api.get<IPaymentLimit>(url, {
      params: {
        token,
      },
    });

    return data;
  }

  async getBanner() {
    try {
      const url = "/api/banners/current";
      const { data } = await this._api.get(url);
      appStore.setState({ banner: data });
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }
}

export const tokensApi = new TokensService();
